import {Button, IconButton, Typography, makeStyles} from '@material-ui/core';
import {Close} from '@material-ui/icons';
import {Alert} from '@material-ui/lab';
import {SwipeableDrawer} from '@mui/material';
import {isDefined, parseHelperText} from 'Quoting/utils/utilities';
import {themeColors} from 'Theme';
import React, {Fragment, useState} from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';

const useQuoteFormErrorStyles = makeStyles(() => ({
  quoteFormErrorWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  errorTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 2rem',
  },
  errorContainer: {
    marginTop: '1rem',
    padding: '0 2rem',
  },
  errorNotice: {
    color: '#611a15',
    fontSize: '1rem',
    fontWeight: 600,
  },
  fieldError: {
    margin: '0.5rem auto',
    backgroundColor: themeColors.red_50,
    color: `${themeColors.gray_800} !important`,
    '& .MuiAlert-icon': {
      color: themeColors.red_600,
    },
  },
  errorDrawerBtn: {
    borderRadius: '100px',
    maxWidth: '12.5rem',
    background: 'rgba(237, 119, 102, 0.93)',
    zIndex: 1500,
    position: 'absolute',
    left: '50%',
    bottom: '2rem',
    '&:hover': {
      background: 'rgba(237, 119, 102, 0.93)',
    },
  },
  swipeableErrorDrawer: {
    '& .MuiDrawer-paper': {
      maxWidth: '60%',
      padding: '1rem',
      margin: '0 auto',
      borderTopLeftRadius: '4px',
      borderTopRightRadius: '4px',
    },
  },
}));

export const QuoteFormErrorDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const quoteFormErrors = useSelector((state: ReducerState) => state.quoteForm.quoteFormErrors);
  const classes = useQuoteFormErrorStyles();

  const errors = Object.entries(quoteFormErrors).filter(([_, error]) => isDefined(error));

  return (
    <div className={classes.quoteFormErrorWrapper}>
      {!isOpen && errors.length > 0 && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsOpen(!isOpen)}
          className={classes.errorDrawerBtn}
        >
          Show Errors ({errors.length})
        </Button>
      )}
      <SwipeableDrawer
        anchor="bottom"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onOpen={() => setIsOpen(true)}
        disableSwipeToOpen={false}
        className={classes.swipeableErrorDrawer}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {errors.length > 0 ? (
          <Fragment>
            <div className={classes.errorTitle}>
              <Typography className={classes.errorNotice}>Errors ({errors.length})</Typography>
              <IconButton onClick={() => setIsOpen(false)} size="small">
                <Close />
              </IconButton>
            </div>
            <div className={classes.errorContainer}>
              {errors.map(([key, error]) => (
                <QuoteFormError errorKey={key} errorText={error} key={key} className={classes.fieldError} />
              ))}
            </div>
          </Fragment>
        ) : null}
      </SwipeableDrawer>
    </div>
  );
};

type QuoteFormErrorProps = {
  errorKey: string;
  errorText: string;
  className: string;
};

const QuoteFormError = ({errorKey, errorText, className}: QuoteFormErrorProps) => {
  const {quote} = useSelector((state: ReducerState) => state.quoteForm);

  const helperTextForError = parseHelperText(errorKey, errorText, quote);

  return (
    <Alert severity="error" className={className}>
      <Typography>{helperTextForError}</Typography>
    </Alert>
  );
};
